import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FileStack, Trash2, Play, Pencil, Check, X, MessageSquare, Package, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  useLRDirectLoadTemplates,
  useDeleteLRDirectLoadTemplate,
  useRenameLRDirectLoadTemplate,
  type LRDirectLoadTemplate,
} from "@/hooks/useLRDirectLoadTemplates";
import { useInstantPostFromTemplate } from "@/hooks/useInstantPostFromTemplate";
import { formatYYYYMMDDToLocaleDateString } from "@/lib/dateOnly";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";


/**
 * LR Direct ATLAS Command Center — Posting Templates.
 * LR Direct data only (`lr_direct_load_templates`). Completely independent of the
 * LaneRunner ATLAS Hub components (src/components/ai-post/*).
 */

const PAGE_SIZE = 8;

function laneLabel(t: LRDirectLoadTemplate) {
  const o = t.origin_city && t.origin_state ? `${t.origin_city}, ${t.origin_state}` : "—";
  const d = t.destination_city && t.destination_state ? `${t.destination_city}, ${t.destination_state}` : "—";
  return `${o} → ${d}`;
}

function cleanTemplateName(name: string, lane: string) {
  if (!name) return lane;
  const idx = name.indexOf(" · ");
  if (idx > -1) return name.slice(0, idx);
  return name;
}

function titleCase(s: string) {
  return s
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

function Chip({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone: "teal" | "teal-outline" | "amber" | "blue" | "purple" | "neutral";
}) {
  const styles: Record<string, string> = {
    teal: "bg-brand-teal/10 text-brand-teal-deep border border-brand-teal/20",
    "teal-outline": "bg-transparent text-brand-teal-deep border border-brand-teal/40",
    amber: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/20",
    blue: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/20",
    purple: "bg-purple-500/10 text-purple-700 dark:text-purple-400 border border-purple-500/20",
    neutral: "bg-muted text-muted-foreground border border-border",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center h-5 px-1.5 rounded text-[10px] font-semibold uppercase tracking-wide leading-none",
        styles[tone],
      )}
    >
      {children}
    </span>
  );
}

function VisibilityChip({ mode }: { mode: LRDirectLoadTemplate["visibility_mode"] }) {
  if (mode === "private_only") return <Chip tone="teal-outline">Private Network</Chip>;
  if (mode === "open_market") return <Chip tone="blue">Open Market</Chip>;
  return <Chip tone="purple">Both</Chip>;
}

function TemplateSection({
  title,
  icon: Icon,
  items,
  isLoading,
  emptyHint,
  editingId,
  editValue,
  setEditValue,
  startEdit,
  cancelEdit,
  commitEdit,
  renamePending,
  deletePending,
  postingId,
  onPost,
  onDelete,
}: {
  title: string;
  icon: typeof Package;
  items: LRDirectLoadTemplate[];
  isLoading: boolean;
  emptyHint: string;
  editingId: string | null;
  editValue: string;
  setEditValue: (v: string) => void;
  startEdit: (t: LRDirectLoadTemplate) => void;
  cancelEdit: () => void;
  commitEdit: (t: LRDirectLoadTemplate) => void;
  renamePending: boolean;
  deletePending: boolean;
  postingId: string | null;
  onPost: (t: LRDirectLoadTemplate) => void;
  onDelete: (t: LRDirectLoadTemplate) => void;

}) {
  const [page, setPage] = useState(0);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageItems = items.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <Icon className="h-4 w-4 text-brand-teal-deep" />
        <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">{title}</h3>
        <Badge variant="secondary" className="text-[10px]">{items.length}</Badge>
      </div>

      {isLoading ? (
        <div className="p-4 text-xs text-muted-foreground">Loading templates…</div>
      ) : items.length === 0 ? (
        <div className="p-6 text-center">
          <p className="text-xs text-muted-foreground">{emptyHint}</p>
        </div>
      ) : (
        <>
          <ul className="divide-y divide-border">
            {pageItems.map((t) => {
              const lane = laneLabel(t);
              const displayName = cleanTemplateName(t.name, lane);
              const equipment = t.equipment_type ? titleCase(t.equipment_type) : null;
              const isEditing = editingId === t.id;
              return (
                <li key={t.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-muted/30">
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="flex items-center gap-1.5">
                        <Input
                          autoFocus
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              commitEdit(t);
                            } else if (e.key === "Escape") {
                              e.preventDefault();
                              cancelEdit();
                            }
                          }}
                          disabled={renamePending}
                          maxLength={120}
                          className="h-7 text-sm font-semibold"
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => commitEdit(t)}
                          disabled={renamePending}
                          className="h-7 w-7 p-0 text-brand-teal-deep hover:text-brand-teal-deep"
                          title="Save"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={cancelEdit}
                          disabled={renamePending}
                          className="h-7 w-7 p-0 text-muted-foreground"
                          title="Cancel"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => startEdit(t)}
                        className="group flex items-center gap-1.5 text-left max-w-full"
                        title="Rename template"
                      >
                        <span className="text-sm font-semibold text-foreground truncate">{displayName}</span>
                        <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </button>
                    )}
                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] text-muted-foreground truncate">{lane}</span>
                      <VisibilityChip mode={t.visibility_mode} />
                      {equipment && <Chip tone="neutral">{equipment}</Chip>}
                      {t.usage_count > 0 && (
                        <span className="text-[10px] text-muted-foreground ml-1">used {t.usage_count}×</span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => onPost(t)}
                    disabled={isEditing || postingId !== null}
                    className="h-7 text-[11px] gap-1 min-w-[68px]"
                    title="Post instantly with today's pickup date"
                  >
                    {postingId === t.id ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Posting
                      </>
                    ) : (
                      <>
                        <Play className="h-3 w-3" />
                        Post
                      </>
                    )}
                  </Button>

                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDelete(t)}
                    disabled={deletePending || isEditing}
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    title="Delete template"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              );
            })}
          </ul>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2 border-t border-border">
              <span className="text-[10px] text-muted-foreground">
                Page {safePage + 1} of {totalPages}
              </span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={safePage === 0}
                  onClick={() => setPage(Math.max(0, safePage - 1))}
                  className="h-7 text-[11px]"
                >
                  Prev
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={safePage >= totalPages - 1}
                  onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
                  className="h-7 text-[11px]"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function LRDirectTemplatesCard() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [postingId, setPostingId] = useState<string | null>(null);
  const { data: templates = [], isLoading } = useLRDirectLoadTemplates(user?.id);
  const deleteMut = useDeleteLRDirectLoadTemplate();
  const renameMut = useRenameLRDirectLoadTemplate();
  const instantPost = useInstantPostFromTemplate();
  const isDemoUser = (profile as any)?.is_demo_user === true;

  const loads = templates.filter((t) => t.post_type !== "quote");
  const quotes = templates.filter((t) => t.post_type === "quote");

  const startEdit = (t: LRDirectLoadTemplate) => {
    setEditingId(t.id);
    setEditValue(cleanTemplateName(t.name, laneLabel(t)));
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };
  const commitEdit = async (t: LRDirectLoadTemplate) => {
    const next = editValue.trim();
    const current = cleanTemplateName(t.name, laneLabel(t));
    if (!next || next === current) {
      cancelEdit();
      return;
    }
    try {
      await renameMut.mutateAsync({ id: t.id, name: next });
      toast({ title: "Template renamed" });
      cancelEdit();
    } catch (e: any) {
      toast({ variant: "destructive", title: "Rename failed", description: e?.message });
    }
  };

  // Instant post — no second form step. Pickup date is always TODAY.
  const handlePost = async (t: LRDirectLoadTemplate) => {
    if (!user?.id || postingId) return;
    setPostingId(t.id);
    try {
      const res = await instantPost.mutateAsync({ templateId: t.id, userId: user.id, isDemo: isDemoUser });
      const label = res.postType === "quote" ? "Quote" : "Load";
      const where =
        res.visibility === "private_only"
          ? "your private network"
          : res.visibility === "both"
            ? "your network + open market"
            : "the open market";
      toast({
        title: `${label} posted — ${res.lane}`,
        description: `Pickup ${formatYYYYMMDDToLocaleDateString(res.pickupDate, undefined, {
          month: "short",
          day: "numeric",
        })} (today) · sent to ${where}.`,
      });
      navigate(res.postType === "quote" ? "/lr-direct/my-quotes" : `/lr-direct/live/${res.loadId}`);
    } catch (e: any) {
      // Silent-write guard surfaces here — no navigation, real error shown.
      toast({
        variant: "destructive",
        title: "Post failed",
        description: e?.message || "The post was not saved. Please try again.",
      });
    } finally {
      setPostingId(null);
    }
  };

  const handleDelete = async (t: LRDirectLoadTemplate) => {
    if (!confirm(`Delete template "${t.name}"?`)) return;
    try {
      await deleteMut.mutateAsync(t.id);
      toast({ title: "Template deleted" });
    } catch (e: any) {
      toast({ variant: "destructive", title: "Delete failed", description: e?.message });
    }
  };

  const shared = {
    isLoading,
    editingId,
    editValue,
    setEditValue,
    startEdit,
    cancelEdit,
    commitEdit,
    renamePending: renameMut.isPending,
    deletePending: deleteMut.isPending,
    postingId,
    onPost: handlePost,
    onDelete: handleDelete,

  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <FileStack className="h-4 w-4 text-brand-teal-deep" />
        <h2 className="text-sm font-semibold uppercase tracking-wide text-foreground">Posting Templates</h2>
        <Badge variant="secondary" className="text-[10px]">{templates.length}</Badge>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TemplateSection
          title="Loads"
          icon={Package}
          items={loads}
          emptyHint='No load templates yet. Check "Save as template" on the LR Direct post form.'
          {...shared}
        />
        <TemplateSection
          title="Quotes"
          icon={MessageSquare}
          items={quotes}
          emptyHint='No quote templates yet. Check "Save as template" when posting a quote request.'
          {...shared}
        />
      </div>
    </div>
  );
}
